// apps/storefront/src/hooks/useGuestSession.ts
// —————————————————————————————————————————————————————————————————
// Genera y persiste un guestSessionId para usuarios no autenticados.
// Se usa como clave para cart merge al momento del login/register.
// —————————————————————————————————————————————————————————————————

import { useMemo } from "react";

const LS_KEY = "kwad.storefront.guest";

function generateId(): string {
  // nanoid-like: 21 chars, URL-safe
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(36).padStart(2, "0"))
    .join("")
    .slice(0, 21);
}

function getOrCreateGuestId(): string {
  try {
    const existing = localStorage.getItem(LS_KEY);
    if (existing && existing.length > 10) return existing;

    const id = generateId();
    localStorage.setItem(LS_KEY, id);
    return id;
  } catch {
    // SSR / incognito fallback — in-memory only
    return generateId();
  }
}

/** Elimina el guestSessionId (llamar tras login exitoso + merge). */
export function clearGuestSession(): void {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    // no-op
  }
}

/** Lee el guestSessionId actual sin crear uno nuevo. */
export function peekGuestSessionId(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}

/**
 * Hook que devuelve un guestSessionId estable durante el ciclo de vida
 * del componente. Se genera lazy la primera vez que se necesita.
 */
export function useGuestSession() {
  const guestSessionId = useMemo(() => getOrCreateGuestId(), []);

  return { guestSessionId, clearGuestSession, peekGuestSessionId };
}
